<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import BulkBroadcastsAPI from "dashboard/api/bulkBroadcasts";
import Dialog from "dashboard/components-next/dialog/Dialog.vue";
import Icon from "dashboard/components-next/icon/Icon.vue";

const { t } = useI18n();

const POLL_ACTIVE = 3000;
const POLL_IDLE = 10000;

const broadcasts = ref([]);
const dismissedIds = ref([]);
const dialogRef = ref(null);
const actionLoading = ref(false);
let pollTimer = null;

// Los que el usuario no cerró con la X.
const visibleBroadcasts = computed(() =>
  broadcasts.value.filter((b) => !dismissedIds.value.includes(b.id)),
);

const hasActive = computed(() => visibleBroadcasts.value.length > 0);

const totalCount = computed(() =>
  visibleBroadcasts.value.reduce((sum, b) => sum + (b.total_count || 0), 0),
);

const doneCount = computed(() =>
  visibleBroadcasts.value.reduce(
    (sum, b) =>
      sum +
      (b.sent_count || 0) +
      (b.failed_count || 0) +
      (b.skipped_count || 0),
    0,
  ),
);

const failedCount = computed(() =>
  visibleBroadcasts.value.reduce((sum, b) => sum + (b.failed_count || 0), 0),
);

const percentage = computed(() =>
  totalCount.value ? Math.round((doneCount.value / totalCount.value) * 100) : 0,
);

// Estado agregado de la barra (resume varios envíos en una sola barra).
const aggregateStatus = computed(() => {
  if (
    visibleBroadcasts.value.some((b) =>
      ["queued", "running"].includes(b.status),
    )
  ) {
    return "running";
  }
  if (visibleBroadcasts.value.some((b) => b.status === "paused"))
    return "paused";
  return "completed";
});

// Mapa con claves literales para evitar claves i18n dinámicas.
const statusLabels = computed(() => ({
  queued: t("BULK_BROADCAST.PROGRESS.STATUS.queued"),
  running: t("BULK_BROADCAST.PROGRESS.STATUS.running"),
  paused: t("BULK_BROADCAST.PROGRESS.STATUS.paused"),
  completed: t("BULK_BROADCAST.PROGRESS.STATUS.completed"),
  failed: t("BULK_BROADCAST.PROGRESS.STATUS.failed"),
  stopped: t("BULK_BROADCAST.PROGRESS.STATUS.stopped"),
  scheduled: t("BULK_BROADCAST.PROGRESS.STATUS.scheduled"),
  expired: t("BULK_BROADCAST.PROGRESS.STATUS.expired"),
}));

const barLabel = computed(() => statusLabels.value[aggregateStatus.value]);

const barColorClass = computed(() => {
  if (aggregateStatus.value === "running")
    return "bg-blue-600 dark:bg-blue-500";
  if (aggregateStatus.value === "paused") return "bg-amber-500";
  return failedCount.value ? "bg-amber-500" : "bg-green-600 dark:bg-green-500";
});

const iconColorClass = computed(() => {
  if (aggregateStatus.value === "running")
    return "text-blue-600 dark:text-blue-400";
  if (aggregateStatus.value === "paused") return "text-amber-500";
  return failedCount.value
    ? "text-amber-500"
    : "text-green-600 dark:text-green-400";
});

const broadcastPercent = (broadcast) => {
  const total = broadcast.total_count || 0;
  if (!total) return 0;
  const done =
    (broadcast.sent_count || 0) +
    (broadcast.failed_count || 0) +
    (broadcast.skipped_count || 0);
  return Math.round((done / total) * 100);
};

const broadcastColorClass = (broadcast) => {
  switch (broadcast.status) {
    case "completed":
      return "bg-green-600 dark:bg-green-500";
    case "failed":
      return "bg-red-600 dark:bg-red-500";
    case "stopped":
      return "bg-slate-400 dark:bg-slate-500";
    case "expired":
      return "bg-red-400 dark:bg-red-400";
    case "paused":
      return "bg-amber-500";
    default:
      return "bg-blue-600 dark:bg-blue-500";
  }
};

const statusLabel = (broadcast) =>
  statusLabels.value[broadcast.status] || broadcast.status;

const fetchActive = async () => {
  try {
    const { data } = await BulkBroadcastsAPI.getActive();
    // El index devuelve { payload, meta }; se tolera el array plano para que un
    // bundle viejo contra un server nuevo no deje la barra vacía.
    broadcasts.value = Array.isArray(data) ? data : (data?.payload ?? []);
    // Limpia descartados que ya no están (así un envío nuevo reaparece).
    const presentIds = broadcasts.value.map((b) => b.id);
    dismissedIds.value = dismissedIds.value.filter((id) =>
      presentIds.includes(id),
    );
  } catch (error) {
    // Silencioso: es un proceso de fondo, no debe interrumpir la UI.
    broadcasts.value = [];
  }
};

async function poll() {
  if (!document.hidden) await fetchActive();
  const delay = broadcasts.value.length ? POLL_ACTIVE : POLL_IDLE;
  pollTimer = setTimeout(poll, delay);
}

const openDialog = () => dialogRef.value?.open();

const dismiss = () => {
  const ids = visibleBroadcasts.value.map((b) => b.id);
  dismissedIds.value = [...new Set([...dismissedIds.value, ...ids])];
  dialogRef.value?.close();
};

// can_manage lo resuelve el backend (admin o creador). Sin este guard el agente
// clickea y come un 403 silencioso: controlBroadcast traga el error.
const canPause = (b) => b.can_manage && ["queued", "running"].includes(b.status);
const canResume = (b) => b.can_manage && b.status === "paused";
const canStop = (b) =>
  b.can_manage && ["queued", "running", "paused"].includes(b.status);

const controlBroadcast = async (broadcast, action) => {
  if (
    action === "stop" &&
    // eslint-disable-next-line no-alert
    !window.confirm(t("BULK_BROADCAST.PROGRESS.STOP_CONFIRM"))
  ) {
    return;
  }
  actionLoading.value = true;
  try {
    await BulkBroadcastsAPI[action](broadcast.id);
    await fetchActive();
  } catch (error) {
    // Silencioso: no interrumpir la UI.
  } finally {
    actionLoading.value = false;
  }
};

onMounted(() => {
  poll();
});

onBeforeUnmount(() => {
  if (pollTimer) clearTimeout(pollTimer);
});
</script>

<template>
  <div class="contents">
    <div
      v-if="hasActive"
      class="flex items-center gap-3 w-full px-4 py-1.5 text-xs bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700"
    >
      <Icon
        icon="i-lucide-send"
        class="shrink-0"
        :class="iconColorClass"
        size="14"
      />
      <span class="font-medium text-slate-700 dark:text-slate-200 shrink-0">
        {{ barLabel }}
      </span>

      <div
        class="flex-1 h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
      >
        <div
          class="h-full transition-all duration-500"
          :class="barColorClass"
          :style="{ width: `${percentage}%` }"
        />
      </div>

      <span class="tabular-nums text-slate-600 dark:text-slate-300 shrink-0">
        {{
          $t("BULK_BROADCAST.PROGRESS.COUNT_SUMMARY", {
            done: doneCount,
            total: totalCount,
          })
        }}
      </span>
      <span
        v-if="failedCount"
        class="tabular-nums text-red-600 dark:text-red-400 shrink-0"
      >
        {{
          $t("BULK_BROADCAST.PROGRESS.FAILED_INLINE", { count: failedCount })
        }}
      </span>

      <button
        type="button"
        class="shrink-0 px-2 py-0.5 rounded-md font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30"
        @click="openDialog"
      >
        {{ $t("BULK_BROADCAST.PROGRESS.VIEW_DETAILS") }}
      </button>
      <button
        type="button"
        :title="$t('BULK_BROADCAST.PROGRESS.CLOSE')"
        class="shrink-0 p-0.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700"
        @click="dismiss"
      >
        <Icon icon="i-lucide-x" size="14" />
      </button>

      <Dialog
        ref="dialogRef"
        type="edit"
        width="lg"
        :title="$t('BULK_BROADCAST.PROGRESS.MODAL_TITLE')"
        :show-confirm-button="false"
        :cancel-button-label="$t('BULK_BROADCAST.PROGRESS.CLOSE')"
        overflow-y-auto
      >
        <div
          v-if="!visibleBroadcasts.length"
          class="py-6 text-sm text-center text-slate-500"
        >
          {{ $t("BULK_BROADCAST.PROGRESS.EMPTY") }}
        </div>
        <div v-else class="flex flex-col gap-4 max-h-96 overflow-y-auto">
          <div
            v-for="broadcast in visibleBroadcasts"
            :key="broadcast.id"
            class="flex flex-col gap-2 p-3 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <div class="flex items-center justify-between gap-2">
              <span
                class="text-sm font-medium text-slate-900 dark:text-white truncate"
              >
                {{
                  broadcast.campaign_name ||
                  $t("BULK_BROADCAST.PROGRESS.NO_CAMPAIGN_NAME", {
                    id: broadcast.id,
                  })
                }}
              </span>
              <span
                class="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 shrink-0"
              >
                {{ statusLabel(broadcast) }}
              </span>
            </div>

            <div
              class="h-1.5 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden"
            >
              <div
                class="h-full transition-all duration-500"
                :class="broadcastColorClass(broadcast)"
                :style="{ width: `${broadcastPercent(broadcast)}%` }"
              />
            </div>

            <div
              class="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-600 dark:text-slate-300 tabular-nums"
            >
              <span>
                {{
                  $t("BULK_BROADCAST.PROGRESS.TOTAL_COUNT", {
                    count: broadcast.total_count,
                  })
                }}
              </span>
              <span class="text-green-600 dark:text-green-400">
                {{
                  $t("BULK_BROADCAST.PROGRESS.SENT_COUNT", {
                    count: broadcast.sent_count,
                  })
                }}
              </span>
              <span class="text-red-600 dark:text-red-400">
                {{
                  $t("BULK_BROADCAST.PROGRESS.FAILED_COUNT", {
                    count: broadcast.failed_count,
                  })
                }}
              </span>
              <span>
                {{
                  $t("BULK_BROADCAST.PROGRESS.PENDING_COUNT", {
                    count: broadcast.pending_count,
                  })
                }}
              </span>
            </div>

            <div v-if="canStop(broadcast)" class="flex gap-2 pt-1">
              <button
                v-if="canPause(broadcast)"
                type="button"
                :disabled="actionLoading"
                class="px-2 py-1 rounded-md text-xs font-medium text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 disabled:opacity-50"
                @click="controlBroadcast(broadcast, 'pause')"
              >
                {{ $t("BULK_BROADCAST.PROGRESS.PAUSE") }}
              </button>
              <button
                v-if="canResume(broadcast)"
                type="button"
                :disabled="actionLoading"
                class="px-2 py-1 rounded-md text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 disabled:opacity-50"
                @click="controlBroadcast(broadcast, 'resume')"
              >
                {{ $t("BULK_BROADCAST.PROGRESS.RESUME") }}
              </button>
              <button
                type="button"
                :disabled="actionLoading"
                class="px-2 py-1 rounded-md text-xs font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 dark:hover:bg-red-900/50 disabled:opacity-50"
                @click="controlBroadcast(broadcast, 'stop')"
              >
                {{ $t("BULK_BROADCAST.PROGRESS.STOP") }}
              </button>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  </div>
</template>
